<template>
    <div>
        <div
            v-for="(user, index) in selectedUsers"
            :key="user.id"
            :class="[{ 'tw-mb-4': index !== selectedUsers.length - 1 }]"
        >
            <SelectPackageCard
                :hideHint="true"
                :allMemberships="[]"
                :myMemberships="getPackages(user.id)"
                :selectedUserPkg="getSelectedUserPkg(user.id)"
                :studioClass="props.studioClass"
                :packageCount="getPkgCount(user.id)"
                @onSelectPackage="
                    (pkg) =>
                        updateSelectedUserPackage({
                            pkg,
                            userId: user.id,
                        })
                "
                :user="user"
                bookingType="family"
            >
            </SelectPackageCard>
        </div>
    </div>
</template>

<script setup lang="ts">
import { getPackagesForSubAccount } from './api/getPackagesForSubAccountApi'
import { ref, onMounted, computed, watch, type Ref } from 'vue'
import {
    RecurringPackageStatus,
    type Package,
    type SelectedUserPackage,
    type StudioClass,
    type User,
} from './types/types'
import { getClassSignups } from '@/util/classes/getClassSignups'
import SelectPackageCard from './SelectPackageCard.vue'

const props = defineProps<{
    studioClass: StudioClass
    userId: string
    selectedFamilyUsers: User[]
    userPkgs: Package[]
    mainAccount: User
    subAccounts: User[]
}>()

const emit = defineEmits<{
    (
        e: 'updateSelectedPackages',
        selectedPkgs: SelectedUserPackage[] | undefined
    ): void
    (e: 'onExistingSignupFamily', users: User[]): void
}>()

const allUserPkgs = ref([]) as Ref<Package[]>
const allUsers = ref([]) as Ref<User[]>

async function init() {
    if (props.userPkgs) {
        const filteredPkgs = filterValidPkgs(props.userPkgs)
        allUserPkgs?.value?.push(...filteredPkgs)
    }
    if (props.mainAccount) {
        allUsers.value = [props.mainAccount]
    }
    if (props.subAccounts) {
        allUsers?.value?.push(...props.subAccounts)
        await reloadAccountPkgs(props.subAccounts)
    }

    const signups = await fetchSignups(allUsers.value)
    const signupUsers = allUsers.value.filter((user) =>
        signups.some((signup) => signup.userId === user.id)
    )
    emit('onExistingSignupFamily', signupUsers)
}

async function reloadAccountPkgs(users: User[]) {
    const pkgs = await fetcAccountPkgs(users)
    // only push if not already in the list
    const filteredPkgs = pkgs.filter(
        (pkg) => !allUserPkgs?.value?.some((p) => p.id === pkg.id)
    )
    allUserPkgs?.value?.push(...filteredPkgs)
}

async function fetcAccountPkgs(users: User[]) {
    const packages = []
    for (const user of users) {
        const response = await getPackagesForSubAccount(
            props.userId,
            user.id,
            props.studioClass.studioId
        )
        if (!response.success) {
            return []
        }

        const filteredPkgs = filterValidPkgs(response.packages)
        packages.push(...filteredPkgs)
    }
    return packages
}

function filterValidPkgs(pkgs: Package[]) {
    return pkgs.filter((pkg) => {
        const hasCredits = pkg.classesLeft > 0
        if (pkg.isSubscription) {
            const isActive = pkg.status === RecurringPackageStatus.ACTIVE
            return isActive && hasCredits
        }
        return hasCredits
    })
}

type ClassSignup = {
    id: string
    userId: string
    classId: string
    spotNumber: number
}

async function fetchSignups(users: User[]): Promise<ClassSignup[]> {
    const res = await Promise.all(
        users.map(async (user) =>
            getClassSignups(props.studioClass.id, user.id)
        )
    )

    if (!res || !res.length) {
        return []
    }

    return res.flat()
}

onMounted(() => {
    init()
})

const selectedUsers = computed(() => {
    if (!props.selectedFamilyUsers) {
        return []
    }

    const selectedUserIds = props.selectedFamilyUsers.map((user) => user.id)
    return allUsers?.value?.filter((user) => selectedUserIds.includes(user.id))
})

// packages
const selectedUserPkgs = ref<SelectedUserPackage[]>()
if (!selectedUserPkgs.value) {
    selectedUserPkgs.value = []
}

watch(
    () => props.selectedFamilyUsers,
    (newValue) => {
        const currentSelectedPackages = selectedUserPkgs.value
        if (!currentSelectedPackages || currentSelectedPackages.length === 0)
            return

        if (!newValue || newValue.length === 0) {
            selectedUserPkgs.value = []
            emit('updateSelectedPackages', selectedUserPkgs.value)
            return
        }

        let shouldUpdate = false
        const tempSelectedUserPkgs = []
        for (let i = 0; i < currentSelectedPackages.length; i++) {
            const isFound = newValue.find(
                (x) => currentSelectedPackages[i].userId === x.id
            )

            if (!isFound) {
                shouldUpdate = true
            } else {
                tempSelectedUserPkgs.push(currentSelectedPackages[i])
            }
        }

        if (shouldUpdate) {
            selectedUserPkgs.value = tempSelectedUserPkgs
            emit('updateSelectedPackages', selectedUserPkgs.value)
        }
    },
    { deep: true }
)

function getPackages(userId: string) {
    return allUserPkgs.value?.filter((pkg) => pkg.userId === userId)
}

function getSelectedUserPkg(userId: string) {
    return selectedUserPkgs.value?.find((pkg) => pkg.userId === userId)
        ?.packageId
}

function updateSelectedUserPackage(req: { pkg: Package; userId: string }) {
    // remove existing selection
    selectedUserPkgs.value = selectedUserPkgs.value?.filter(
        (pkg) => pkg.userId !== req.userId
    )
    selectedUserPkgs.value?.push({
        packageId: req.pkg.id,
        userId: req.userId,
    })

    emit('updateSelectedPackages', selectedUserPkgs.value)
}

function getPkgCount(userId: string) {
    const pkgs = getPackages(userId)
    return pkgs?.length ?? 0
}
</script>
